import { CSSObject, Drawer as MuiDrawer, styled, Theme } from '@mui/material';
import { ReactNode } from 'react';

import { useResolution } from '../../hooks';
import SidebarItem from './SidebarItem';
import SidebarLogo from './SidebarLogo';
import SidebarMenu from './SidebarMenu';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.enteringScreen,
    easing: theme.transitions.easing.sharp,
  }),
  width: drawerWidth,
});

const closedMixin = (theme: Theme): CSSObject => ({
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.leavingScreen,
    easing: theme.transitions.easing.sharp,
  }),
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
  boxSizing: 'border-box',
  flexShrink: 0,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
  whiteSpace: 'nowrap',
  width: drawerWidth,
}));

type SidebarProps = {
  children: ReactNode;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
};

const Sidebar = ({ children, isSidebarOpen, toggleSidebar }: SidebarProps) => {
  const { isDesktop, isTablet } = useResolution();

  if (isDesktop || isTablet) {
    return (
      <Drawer variant="permanent" open={isSidebarOpen}>
        {children}
      </Drawer>
    );
  }

  return (
    <MuiDrawer variant="temporary" open={isSidebarOpen} onClose={toggleSidebar}>
      {children}
    </MuiDrawer>
  );
};

Sidebar.Item = SidebarItem;
Sidebar.Logo = SidebarLogo;
Sidebar.Menu = SidebarMenu;

export default Sidebar;
